<!-- BUG: when the master switch is checked and form is submitted
          the master switch and group switches are toggled off but the rest are toggled -->
<template>
  <label v-if="label" :for="inputId" :class="labelClass">{{ label }}</label>
  <div>
    <div :class="containerClasses">
      <ToggleSwitch
        ref="toggleRef"
        :pt="{
          root: { class: `switch-container ${toggleClasses} items-center mr-0 relative inline-flex h-6 w-12 shrink-0 cursor-pointer rounded-full border-3 border-transparent transition-colors duration-200 ease-in-out` },
          slider: { class: `slider-circle ${sliderClasses} absolute pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out` },
          input: { 
            name: props.name,
            id: inputId,
            value: '1',
            ...props.data
          }
        }"
        v-model="checked"
        :aria-label="srLabel"
        @change="handleBothToggles"
      />
      <i :class="[
          'absolute top-1/2 -translate-y-1/2 text-[10px] text-white pointer-events-none transition-all duration-200',
          checked ? 'left-2' : 'right-2',
          iconClass
        ]"></i>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, onUnmounted } from 'vue'
  import ToggleSwitch from 'primevue/toggleswitch'
  import { eventBus } from '../xinspire/event_bus'

  const props = defineProps({
    name:           { type: String },
    data:           { type: String, default: '' },
    initialChecked: { type: Boolean, default: false },
    isMasterSwitch: { type: Boolean, default: false },
    isGroupSwitch:  { type: Boolean, default: false },
    groupSwitchId:  { type: String },
    label:          { type: String },
    srLabel:        { type: String, default: 'Enable field-level privacy control' },
  })

  const checked = ref(props.initialChecked)
  const toggleRef = ref(null)

  const inputId = computed(() =>
    props.name ? props.name.replace(/[^a-zA-Z0-9_-]/g, '_') : ''
  )

  const containerClasses = computed(() => ({
    'toggle-switch relative !w-fit': true,
    'flex flex-col items-start': !props.isMasterSwitch && !props.isGroupSwitch,
    'user-privacy-group-switch': props.isGroupSwitch,
    'user-privacy-master-switch': props.isMasterSwitch,
  }))

  const groupMasterSwitch = computed(() => {
    
  })

  const labelClass = computed(() => ({
    'text-xs text-center pt-1': true,
    '!w-auto !mr-2': props.isGroupSwitch || props.isMasterSwitch,
    'hidden': !props.isMasterSwitch && !props.isGroupSwitch
  }))

  const sliderClasses = computed(() =>
    `transition-all duration-200 ease-in-out ${checked.value ? 'translate-x-6' : 'translate-x-0'}`
  )

  const toggleClasses = computed(() =>
    `transition-colors duration-200 ease-in-out ${checked.value ? 'bg-blue-500' : 'bg-gray-200'}`
  )

  const iconClass = computed(() => {
    return checked.value ? 'fas fa-fw fa-eye-slash' : 'fas fa-fw fa-eye'
  })

  // Called when master switch toggled
  const handleMasterToggle = () => {
    eventBus.emit('toggleAllSwitches', checked.value)
  }

  // Called when group master toggled
  const handleGroupToggle = () => {
    if (!props.isGroupSwitch) return

    const toggleInput = toggleRef.value?.$el?.querySelector('input[type="checkbox"]')
    if (!toggleInput) return

    const parentElement = toggleInput.closest('.n-group')
    if (!parentElement) return

    const fieldName = parentElement.getAttribute('data-field')

    // Emit event to update only this group toggles
    eventBus.emit('toggleGroupOnly', { fieldName, checked: toggleInput.checked })
  }

  // Called on toggle change for all toggles (master, group, individual)
  const handleBothToggles = () => {
    if (props.isMasterSwitch) {
      handleMasterToggle()
    } else if (props.isGroupSwitch) {
      handleGroupToggle()
    }
    // individual toggles do not emit events
  }

  // Update for all toggles when master switch changes
  const updateAllSwitches = (state) => {
    if (props.isMasterSwitch) return // ignore master itself

    checked.value = state
  }

  // Update for toggles when group master changes - only toggles inside group update
  const updateGroupOnlySwitches = ({ fieldName, checked: groupChecked }) => {
    if (props.isMasterSwitch) return // master ignores

    // Find this toggle's group
    const toggleInput = toggleRef.value?.$el?.querySelector('input[type="checkbox"]')
    if (!toggleInput) return

    const parentElement = toggleInput.closest('.n-group')
    if (!parentElement) return

    // Check if toggle belongs to this group
    if (parentElement.getAttribute('data-field') === fieldName) {
      checked.value = groupChecked
    }
  }

  // When master switch is toggled, all switches will toggle
  onMounted(() => {
    if (props.isMasterSwitch || props.isGroupSwitch || !props.isGroupSwitch) {
      eventBus.on('toggleAllSwitches', updateAllSwitches)
      eventBus.on('toggleGroupOnly', updateGroupOnlySwitches)
    }
  })

  onUnmounted(() => {
    if (props.isMasterSwitch || props.isGroupSwitch || !props.isGroupSwitch) {
      eventBus.off('toggleAllSwitches', updateAllSwitches)
      eventBus.off('toggleGroupOnly', updateGroupOnlySwitches)
    }
  })
</script>
